import { fetchCards } from './api';
import { CARD_THEME_MAP } from './config';
import { ChatView } from './chatView';
import { clearElement, createCardTile } from './dom';
import { ImageView } from './image/view';
import { ParentModeController } from './parentMode';
import { ChatStore } from './storage';
import { VideoModalController } from './video/modal';
import type { CardSummary, ImageUiCopy, LocaleCode } from '../src/shared/contracts';

interface AppView {
  destroy(): void;
  mount(): void;
}

export class KaiApp {
  private readonly store = new ChatStore();
  private readonly cardsContainer = document.getElementById('cards-container') as HTMLElement;
  private readonly languageButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('.language-btn'));
  private readonly videoModal = new VideoModalController(
    document.getElementById('videoPlayerModal') as HTMLElement,
  );
  private readonly parentMode = new ParentModeController({
    trigger: document.getElementById('app-title') as HTMLElement,
    modal: document.getElementById('parentModeModal') as HTMLElement,
    getLocale: () => this.store.getLanguage(),
    getTimeLeft: () => this.videoModal.getTimeLeft(),
    getWatchedSeconds: () => this.videoModal.getWatchedSeconds(),
    resetWatchBudget: () => this.videoModal.resetWatchBudget(),
  });
  private cards = new Map<string, CardSummary>();
  private activeView: AppView | null = null;
  private imageServiceBaseUrl: string | null = null;
  private imageUi: ImageUiCopy | null = null;

  async init(): Promise<void> {
    this.bindLanguageButtons();
    window.addEventListener('hashchange', () => this.syncFromHash());

    const lang = this.store.getLanguage();
    this.updateLanguageButtons(lang);
    await this.loadCards(lang);
    this.syncFromHash();
  }

  private bindLanguageButtons(): void {
    for (const button of this.languageButtons) {
      button.addEventListener('click', async () => {
        const lang = button.dataset.lang as LocaleCode | undefined;
        if (!lang || lang === this.store.getLanguage()) {
          return;
        }

        this.store.setLanguage(lang);
        this.updateLanguageButtons(lang);
        window.location.hash = '';
        this.closeActiveView();
        await this.loadCards(lang);
      });
    }
  }

  private updateLanguageButtons(lang: LocaleCode): void {
    for (const button of this.languageButtons) {
      button.classList.toggle('active', button.dataset.lang === lang);
    }
  }

  private async loadCards(lang: LocaleCode): Promise<void> {
    try {
      const payload = await fetchCards(lang);
      this.videoModal.setMaxDailySeconds(payload.appConfig.youtubeAllowanceSecondsPerDay);
      this.imageServiceBaseUrl = payload.appConfig.imageServiceBaseUrl;
      this.imageUi = payload.imageUi;
      this.cards = new Map(payload.cards.map((card) => [card.id, card]));
      this.renderCards(payload.cards);
    } catch (error) {
      clearElement(this.cardsContainer);
      const errorMessage = document.createElement('div');
      errorMessage.className = 'col-span-full text-center text-red-500';
      errorMessage.textContent = error instanceof Error ? error.message : 'Unable to load cards.';
      this.cardsContainer.append(errorMessage);
    }
  }

  private renderCards(cards: CardSummary[]): void {
    clearElement(this.cardsContainer);

    if (cards.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'col-span-full text-center text-gray-500';
      empty.textContent = 'No cards available.';
      this.cardsContainer.append(empty);
      return;
    }

    for (const card of cards) {
      const theme = CARD_THEME_MAP[card.color];
      const tile = createCardTile(card, theme.tile);
      tile.addEventListener('click', () => {
        window.location.hash = card.id;
      });
      this.cardsContainer.append(tile);
    }
  }

  private syncFromHash(): void {
    const cardId = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    if (!cardId) {
      this.closeActiveView();
      return;
    }

    const card = this.cards.get(cardId);
    if (!card) {
      this.closeActiveView();
      return;
    }

    this.activeView?.destroy();

    if (card.experience === 'image') {
      if (!this.imageServiceBaseUrl || !this.imageUi) {
        this.activeView = null;
        return;
      }
      this.activeView = new ImageView({
        card,
        copy: this.imageUi,
        imageServiceBaseUrl: this.imageServiceBaseUrl,
        onClose: () => {
          window.location.hash = '';
        },
      });
    } else {
      this.activeView = new ChatView({
        card,
        lang: this.store.getLanguage(),
        store: this.store,
        videoModal: this.videoModal,
        onClose: () => {
          window.location.hash = '';
        },
      });
    }

    this.activeView.mount();
  }

  private closeActiveView(): void {
    this.activeView?.destroy();
    this.activeView = null;
  }
}
